import React, { useState } from "react";
import { BaseModal, IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import { useTranslation } from "react-i18next";
import styles from "./ImportExpressionsModal.module.scss";
import { FileDropzone } from "@whatslly/ui-components/v2/FileDropzone";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { Button } from "@whatslly/ui-components/v2/Button";
import { downloadBlob } from "@whatslly/ui-components/componentsUtils";
import { arrayToCsv } from "@whatslly/commons/csvUtils";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import classnames from "classnames";
import { FaTrash } from "react-icons/fa";
import globalStyles from "@whatslly/ui-components/v2/globalStyles";
import { Text } from "@whatslly/ui-components/Text";
import { calFileSizeV2 } from "@whatslly/commons/utils";

const cols = [
  {
    name: "Expression",
    required: true,
  },
  {
    name: "Case Sensitive (true or false)",
    required: false,
  },
  {
    name: "Max Hits",
    required: false,
  },
  {
    name: "Count Unique (true or false)",
    required: false,
  },
];

const downloadCsvTemplate = () => {
  downloadBlob(arrayToCsv([[...cols.map((col) => col.name)]]), `tuvis-csv-template.csv`, "text/csv;charset=utf-8;");
};

export const ImportExpressionsModal: React.FC<IModalInstance<{ csvContent: string | undefined }>> = ({ closeModal }) => {
  const { t } = useTranslation();
  const [csvContent, setCsvContent] = useState<string | undefined>();
  const [selectedFile, setSelectedFile] = useState<File | undefined>();

  return (
    <BaseModal
      closeModal={closeModal}
      className={styles.importUsersModal}
      bodyClassName={styles.importUsersModalBody}
      header={<ModalHeader closeModal={() => closeModal()}>{t("user_management.import_users_csv_button")}</ModalHeader>}
      footer={
        <ModalButtons
          submitText={t("user_management.import_button")}
          testId={`modal-import-button`}
          onCancel={() => closeModal()}
          onSubmit={() => closeModal({ csvContent })}
        />
      }
    >
      <div>{t("user_management.import_users_modal_text")}</div>
      <ul>
        {cols.map((col) => {
          return <li key={col.name}>{`${col.name}${col.required ? ` (${t("user_management.required")})` : ""}`}</li>;
        })}
      </ul>
      <div>
        <Button
          secondary
          data-testid={`download-csv-template-button`}
          onClick={downloadCsvTemplate}
          paddingInline={`small`}
          text={t("user_management.import_users_csv_template")}
        />
      </div>
      {selectedFile ? (
        <div
          className={classnames(
            styles.clearAndText,
            globalStyles["bg-color-secondary-primary"],
            globalStyles["bc-neutral-secondary"],
            globalStyles["color-main-primary"]
          )}
        >
          <div className={styles.textContainer}>
            <div className={styles.fileNameText}>{selectedFile.name}</div>
            <p className={classnames(styles.sizeText, globalStyles["color-neutral-primary"])}>({calFileSizeV2(selectedFile)})</p>
          </div>
          <div
            className={styles.clearIconContainer}
            onClick={() => {
              setCsvContent(undefined);
              setSelectedFile(undefined);
            }}
          >
            <Text className={classnames(styles.clearIcon, globalStyles["color-main-primary"])}>
              <FaTrash width="14" height="14"></FaTrash>
            </Text>
          </div>
        </div>
      ) : (
        <FileDropzone
          accept=".csv"
          mode="string"
          className={styles.dropzone}
          onFile={(csvContent, file) => {
            setCsvContent(csvContent);
            setSelectedFile(file);
          }}
        >
          <SvgV2 iconName="upload" height={25} />
          <div>{t("user_management.drop_csv")}</div>
        </FileDropzone>
      )}
    </BaseModal>
  );
};
